import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { createTaskApi } from "../api/taskApi";
import { createTaskTemplateApi } from "../api/taskTemplateApi";
import { applyBoardColumn, isTaskPaused, taskBoardColumnKey } from "../constants";
import { isDueTodayOrOverdue, sortTasksForStaff, isDueToday, isOverdue } from "../utils/taskDates";
import { parseChecklist, toggleChecklistItem as flipChecklistItem, checklistBlocksComplete } from "../utils/taskChecklist";
import { detectNewTasks, notifyNewTask } from "../utils/taskNotifications";
import {
    syncTaskRemindersFromTasks,
    cancelTaskReminder,
    handleTaskReminderDeepLink,
    isRecurringTask,
    findMissedReminders,
    notifyMissedReminders,
} from "../utils/taskReminderSync";
import { useTaskEvents } from "./useTaskEvents";
import { startTimer, pauseTimer } from "../utils/taskTimer";
import { nextNowTask } from "../utils/taskQueue";
import { taskToTemplatePayload } from "../utils/taskTemplates";
import { isUnassigned } from "../utils/taskTriage";

const POLL_MS = 30_000;
const POLL_MS_REALTIME = 90_000;

export function useTasks({ role, apiUrl, getToken, enablePolling = true }) {
    const api = useMemo(() => createTaskApi({ role, apiUrl, getToken }), [role, apiUrl, getToken]);
    const templateApi = useMemo(
        () => createTaskTemplateApi({ apiUrl, getToken }),
        [apiUrl, getToken]
    );
    const isStaff = role === "staff";

    const [tasks, setTasks] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");
    const [priorityFilter, setPriorityFilter] = useState("");
    const [viewFilter, setViewFilter] = useState(isStaff ? "today" : "all");
    const [dueFilter, setDueFilter] = useState("");
    const [assigneeFilter, setAssigneeFilter] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [sortBy, setSortBy] = useState("");
    const [selectedTask, setSelectedTask] = useState(null);
    const [completedOpen, setCompletedOpen] = useState(false);
    const [realtimeConnected, setRealtimeConnected] = useState(false);
    const [missedReminders, setMissedReminders] = useState([]);
    const [snoozingId, setSnoozingId] = useState(null);
    const pollReadyRef = useRef(false);

    const refreshTasks = useCallback(
        async (silent = false) => {
            try {
                const data = await api.fetchTasks();
                setTasks((prev) => {
                    if (silent && pollReadyRef.current && isStaff) {
                        const newOnes = detectNewTasks(prev, data);
                        newOnes.forEach((t) => notifyNewTask(t, { role }));
                    }
                    pollReadyRef.current = true;
                    return data;
                });
                // Keep detail drawer in sync after poll
                setSelectedTask((prev) => {
                    if (!prev) return prev;
                    const updated = data.find((t) => t.id === prev.id);
                    return updated ?? prev;
                });
                syncTaskRemindersFromTasks(data, { role }).catch(() => {});
                const missed = findMissedReminders(data, role);
                setMissedReminders(missed);
                notifyMissedReminders(data, {
                    role,
                    onOpenTask: (task) => setSelectedTask(task),
                });
            } catch (error) {
                if (!silent) toast.error(error.message);
            }
        },
        [api, isStaff, role]
    );

    const fetchTasks = useCallback(() => refreshTasks(false), [refreshTasks]);

    const handleRealtimeEvent = useCallback(
        (payload) => {
            if (payload.type === "connected") {
                setRealtimeConnected(true);
                return;
            }
            if (payload.type === "disconnected") {
                setRealtimeConnected(false);
                return;
            }
            if (payload.type === "task:created" && isStaff && payload.task) {
                notifyNewTask(payload.task, { role });
            }
            refreshTasks(true);
        },
        [isStaff, role, refreshTasks]
    );

    useTaskEvents({
        apiUrl,
        getToken,
        enabled: enablePolling,
        onEvent: handleRealtimeEvent,
    });

    useEffect(() => {
        refreshTasks(false);
    }, [refreshTasks]);

    useEffect(() => {
        if (!enablePolling) return undefined;
        const pollMs = realtimeConnected ? POLL_MS_REALTIME : POLL_MS;
        const tick = () => {
            if (!document.hidden) refreshTasks(true);
        };
        const id = setInterval(tick, pollMs);
        document.addEventListener("visibilitychange", tick);
        return () => {
            clearInterval(id);
            document.removeEventListener("visibilitychange", tick);
        };
    }, [enablePolling, refreshTasks, realtimeConnected]);

    const filteredTasks = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        let list = tasks.filter((t) => {
            const paused = isTaskPaused(t);
            const statusMatch =
                !statusFilter ||
                (statusFilter === "active"
                    ? t.status !== "Completed"
                    : statusFilter === "Paused"
                      ? paused && t.status !== "Completed"
                      : statusFilter === "Blocked"
                        ? String(t.status) === "Blocked"
                        : t.status === statusFilter && !paused);
            const priorityMatch =
                !priorityFilter || t.priority?.toLowerCase() === priorityFilter;
            const todayMatch =
                !isStaff || viewFilter !== "today" || isDueTodayOrOverdue(t);
            const dueMatch =
                !dueFilter ||
                (dueFilter === "today" && isDueToday(t)) ||
                (dueFilter === "overdue" && isOverdue(t)) ||
                (dueFilter === "none" && !t.due_date && t.status !== "Completed") ||
                (dueFilter === "active" && t.status !== "Completed");
            const assigneeMatch =
                !assigneeFilter ||
                (assigneeFilter === "unassigned"
                    ? isUnassigned(t)
                    : String(t.assign_to) === String(assigneeFilter));
            const searchMatch =
                !q ||
                String(t.title || "").toLowerCase().includes(q) ||
                String(t.ListTask?.list_name || "").toLowerCase().includes(q);
            return statusMatch && priorityMatch && todayMatch && dueMatch && assigneeMatch && searchMatch;
        });
        if (sortBy === "due") {
            list = [...list].sort((a, b) => {
                const da = a.due_date ? new Date(a.due_date).getTime() : Infinity;
                const db = b.due_date ? new Date(b.due_date).getTime() : Infinity;
                return da - db;
            });
        } else if (sortBy === "priority") {
            const order = { high: 0, medium: 1, low: 2 };
            list = [...list].sort(
                (a, b) =>
                    (order[a.priority?.toLowerCase()] ?? 1) -
                    (order[b.priority?.toLowerCase()] ?? 1)
            );
        } else if (sortBy === "title") {
            list = [...list].sort((a, b) =>
                String(a.title || "").localeCompare(String(b.title || ""))
            );
        } else if (sortBy === "created") {
            list = [...list].sort((a, b) => Number(b.id) - Number(a.id));
        } else if (isStaff) {
            list = sortTasksForStaff(list);
        }
        return list;
    }, [tasks, statusFilter, priorityFilter, viewFilter, dueFilter, assigneeFilter, searchQuery, sortBy, isStaff]);

    const activeTasks = useMemo(
        () => filteredTasks.filter((t) => t.status !== "Completed"),
        [filteredTasks]
    );

    const completedTasks = useMemo(
        () => filteredTasks.filter((t) => t.status === "Completed"),
        [filteredTasks]
    );

    const todayCount = useMemo(
        () =>
            tasks.filter(
                (t) => t.status !== "Completed" && isDueTodayOrOverdue(t)
            ).length,
        [tasks]
    );

    const updateTaskField = useCallback(
        async (taskId, field, value) => {
            setTasks((prev) =>
                prev.map((t) => (t.id === taskId ? { ...t, [field]: value } : t))
            );
            setSelectedTask((prev) =>
                prev && prev.id === taskId ? { ...prev, [field]: value } : prev
            );
            try {
                const serverUpdates = await api.updateField(taskId, field, value);
                setTasks((prev) =>
                    prev.map((t) => (t.id === taskId ? { ...t, ...serverUpdates } : t))
                );
                setSelectedTask((prev) =>
                    prev && prev.id === taskId ? { ...prev, ...serverUpdates } : prev
                );
            } catch (err) {
                console.error("Update failed", err);
                fetchTasks();
            }
        },
        [api, fetchTasks]
    );

    const handleCompleteTask = useCallback(
        async (id, taskStatus, proofFile = null) => {
            try {
                const task = tasks.find((t) => t.id === id);
                const isCompleting = taskStatus !== "Completed";

                if (isCompleting) {
                    const block = checklistBlocksComplete(task);
                    if (block) {
                        toast.error(block);
                        return false;
                    }
                }

                if (proofFile && isCompleting) {
                    await api.updateField(id, "attachment", proofFile);
                }

                await api.logHistory(
                    id,
                    isCompleting ? "Completed" : "In Progress"
                );

                if (isCompleting && task && isRecurringTask(task)) {
                    if (task) cancelTaskReminder(task);
                    const result = await api.completeOccurrence(id);
                    toast.success(
                        result?.next
                            ? "Task completed — next occurrence scheduled"
                            : proofFile
                              ? "Task completed with photo proof"
                              : "Recurring task completed"
                    );
                } else {
                    await api.updateStatus(id, isCompleting ? "Completed" : "In Progress");
                    if (isCompleting) {
                        toast.success(
                            ({ closeToast }) => (
                                <span className="task-undo-toast">
                                    {proofFile ? "Completed with photo" : "Task completed"}
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            try {
                                                await api.logHistory(id, "In Progress");
                                                await api.updateStatus(id, "In Progress");
                                                await fetchTasks();
                                            } catch (err) {
                                                toast.error(err.message);
                                            }
                                            closeToast();
                                        }}
                                    >
                                        Undo
                                    </button>
                                </span>
                            ),
                            { autoClose: 7000 }
                        );
                    } else {
                        toast.success("Task marked in progress");
                    }
                }

                await fetchTasks();
                if (isStaff && isCompleting) {
                    const next = nextNowTask(tasks, id);
                    setSelectedTask(next);
                    if (next) toast.info(`Next: ${next.title || "Untitled task"}`);
                }
                return true;
            } catch (error) {
                toast.error(error.message);
                return false;
            }
        },
        [api, fetchTasks, tasks, isStaff]
    );

    const handleSnoozeTask = useCallback(
        async (taskId, minutes = 10) => {
            setSnoozingId(taskId);
            try {
                const task = tasks.find((t) => t.id === taskId);
                await api.snoozeTask(taskId, minutes);
                if (task) cancelTaskReminder(task);
                toast.success(`Reminder snoozed for ${minutes} minutes`);
                await fetchTasks();
            } catch (error) {
                toast.error(error.message);
            } finally {
                setSnoozingId(null);
            }
        },
        [api, fetchTasks, tasks]
    );

    const handleTogglePause = useCallback(
        async (taskId, isPaused, reason = null) => {
            try {
                const task = tasks.find((t) => t.id === taskId);
                await api.logHistory(taskId, isPaused ? "Continue Task" : "Paused", reason);
                if (task) {
                    await api.updateField(
                        taskId,
                        "timer",
                        isPaused ? startTimer(task.timer) : pauseTimer(task.timer)
                    );
                }
                await api.updatePaused(taskId, isPaused ? false : true);
                if (!isPaused) {
                    /* pausing current work */
                } else if (String(task?.status || "") !== "In Progress") {
                    await api.updateStatus(taskId, "In Progress");
                }
                toast.success(isPaused ? "Task continued" : "Task paused");
                fetchTasks();
            } catch (error) {
                toast.error(error.message);
            }
        },
        [api, fetchTasks, tasks]
    );

    const moveTaskToColumn = useCallback(
        async (task, columnKey, extras = {}) => {
            if (!task || !columnKey) return;
            if (taskBoardColumnKey(task) === columnKey) return;

            if (columnKey === "Completed") {
                const block = checklistBlocksComplete(task);
                if (block) {
                    toast.error(block);
                    return;
                }
                if (isTaskPaused(task) || task.paused) {
                    try {
                        await api.updatePaused(task.id, false);
                    } catch {
                        /* still attempt complete */
                    }
                }
                await handleCompleteTask(task.id, task.status, extras.proofFile || null);
                return;
            }

            const previous = { status: task.status, paused: task.paused };
            const next = applyBoardColumn(task, columnKey);
            setTasks((prev) => prev.map((t) => (t.id === task.id ? next : t)));
            setSelectedTask((prev) => (prev && prev.id === task.id ? next : prev));

            try {
                if (columnKey === "Paused") {
                    if (String(task.status || "").toLowerCase() === "completed") {
                        await api.logHistory(task.id, "In Progress");
                        await api.updateStatus(task.id, "In Progress");
                    }
                    await api.logHistory(task.id, "Paused", extras.reason || null);
                    await api.updatePaused(task.id, true);
                    toast.success("Moved to Paused");
                } else {
                    if (isTaskPaused(task)) {
                        await api.logHistory(task.id, "Continue Task");
                        await api.updatePaused(task.id, false);
                    }
                    if (task.status !== columnKey) {
                        await api.logHistory(task.id, columnKey);
                        await api.updateStatus(task.id, columnKey);
                    }
                    toast.success(`Moved to ${columnKey}`);
                }
                fetchTasks();
            } catch (error) {
                setTasks((prev) =>
                    prev.map((t) => (t.id === task.id ? { ...t, ...previous } : t))
                );
                setSelectedTask((prev) =>
                    prev && prev.id === task.id ? { ...prev, ...previous } : prev
                );
                toast.error(error.message || "Could not move task");
            }
        },
        [api, fetchTasks, handleCompleteTask]
    );

    const handleDeleteTask = useCallback(
        async (taskId) => {
            const result = await Swal.fire({
                title: "Are you sure?",
                text: "This action cannot be undone!",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#d33",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Yes, delete it!",
                cancelButtonText: "Cancel",
            });

            if (!result.isConfirmed) return;

            try {
                await api.deleteTask(taskId);
                const removed = tasks.find((t) => t.id === taskId);
                if (removed) cancelTaskReminder(removed);
                Swal.fire("Deleted!", "Task has been deleted.", "success");
                setTasks((prev) => prev.filter((t) => t.id !== taskId));
                setSelectedTask((prev) => (prev?.id === taskId ? null : prev));
            } catch (error) {
                Swal.fire("Error!", error.message || "Failed to delete task", "error");
            }
        },
        [api, tasks]
    );

    const updateChecklist = useCallback(
        async (taskId, nextList) => {
            await updateTaskField(taskId, "checklist", parseChecklist(nextList));
        },
        [updateTaskField]
    );

    const uploadChecklistPhoto = useCallback(
        async (taskId, itemId, file) => {
            try {
                const updated = await api.uploadChecklistPhoto(taskId, itemId, file);
                const nextChecklist = updated?.checklist;
                if (nextChecklist) {
                    setTasks((prev) =>
                        prev.map((t) => (t.id === taskId ? { ...t, checklist: nextChecklist } : t))
                    );
                    setSelectedTask((prev) =>
                        prev && prev.id === taskId ? { ...prev, checklist: nextChecklist } : prev
                    );
                } else {
                    await fetchTasks();
                }
            } catch (error) {
                toast.error(error.message || "Could not upload step photo");
            }
        },
        [api, fetchTasks]
    );

    const toggleChecklistItem = useCallback(
        async (taskId, itemId) => {
            const task = tasks.find((t) => t.id === taskId);
            if (!task) return;
            await updateTaskField(
                taskId,
                "checklist",
                flipChecklistItem(task.checklist, itemId, isStaff ? "staff" : "admin")
            );
        },
        [tasks, updateTaskField]
    );

    const addComment = useCallback(
        async (taskId, text) => {
            const body = String(text || "").trim();
            if (!body) return;
            try {
                await api.logHistory(taskId, "Comment", body);
                await fetchTasks();
            } catch (error) {
                toast.error(error.message || "Could not post comment");
            }
        },
        [api, fetchTasks]
    );

    const handlePauseTask = useCallback(
        (task, reason = null) => {
            if (!task) return;
            return handleTogglePause(task.id, isTaskPaused(task), reason);
        },
        [handleTogglePause]
    );

    const handleStartTask = useCallback(
        async (task) => {
            if (!task) return;
            try {
                if (isTaskPaused(task)) {
                    await api.logHistory(task.id, "Continue Task");
                    await api.updatePaused(task.id, false);
                }
                await api.updateField(task.id, "timer", startTimer(task.timer));
                if (String(task.status || "") !== "In Progress") {
                    await api.logHistory(task.id, "In Progress");
                    await api.updateStatus(task.id, "In Progress");
                }
                toast.success("Task started");
                fetchTasks();
            } catch (error) {
                toast.error(error.message);
            }
        },
        [api, fetchTasks]
    );

    const handleDragEnd = useCallback(
        (event) => {
            const { active, over } = event;
            if (!over || active.id === over.id) return;

            setTasks((prev) => {
                const oldIndex = prev.findIndex((t) => t.id === active.id);
                const newIndex = prev.findIndex((t) => t.id === over.id);
                const newTasks = arrayMove(prev, oldIndex, newIndex);
                const orderedTasks = newTasks.map((t, index) => ({
                    id: t.id,
                    position: index,
                }));
                api.reorderTasks(orderedTasks).catch((err) =>
                    console.error("Failed to update order:", err)
                );
                return newTasks;
            });
        },
        [api]
    );

    const saveAsTemplate = useCallback(
        async (task) => {
            if (!task) return;
            const result = await Swal.fire({
                title: "Save as template",
                input: "text",
                inputValue: task.title || "",
                inputPlaceholder: "Opening shift",
                showCancelButton: true,
                confirmButtonText: "Save",
            });
            if (!result.isConfirmed) return;
            const name = String(result.value || "").trim();
            if (!name) {
                toast.error("Template name is required");
                return;
            }
            try {
                await templateApi.createTemplate(taskToTemplatePayload(task, name));
                toast.success("Template saved");
            } catch (error) {
                toast.error(error.message || "Could not save template");
            }
        },
        [templateApi]
    );

    const bulkAssign = useCallback(
        async (taskIds, assignTo) => {
            try {
                await api.bulkAssign(taskIds, assignTo);
                toast.success(`${taskIds.length} task(s) reassigned`);
                fetchTasks();
            } catch (error) {
                toast.error(error.message);
            }
        },
        [api, fetchTasks]
    );

    return {
        tasks,
        setTasks,
        fetchTasks,
        statusFilter,
        setStatusFilter,
        priorityFilter,
        setPriorityFilter,
        viewFilter,
        setViewFilter,
        dueFilter,
        setDueFilter,
        assigneeFilter,
        setAssigneeFilter,
        searchQuery,
        setSearchQuery,
        sortBy,
        setSortBy,
        todayCount,
        selectedTask,
        setSelectedTask,
        completedOpen,
        setCompletedOpen,
        filteredTasks,
        activeTasks,
        completedTasks,
        updateTaskField,
        handleCompleteTask,
        handleSnoozeTask,
        snoozingId,
        handleTogglePause,
        handleStartTask,
        handlePauseTask,
        updateChecklist,
        uploadChecklistPhoto,
        toggleChecklistItem,
        addComment,
        moveTaskToColumn,
        handleDeleteTask,
        handleDragEnd,
        bulkAssign,
        saveAsTemplate,
        realtimeConnected,
        handleTaskReminderDeepLink: (params) =>
            handleTaskReminderDeepLink({ ...params, apiUrl, getToken, role }),
        missedReminders,
        dismissMissedReminder: (taskId) => {
            try {
                const day = new Date().toISOString().slice(0, 10);
                window.localStorage?.setItem(`beentara_missed_reminder_${taskId}_${day}`, "1");
            } catch {
                /* ignore */
            }
            setMissedReminders((prev) => prev.filter((t) => t.id !== taskId));
        },
    };
}
